import { Injectable } from '@angular/core';
import { PopoverController, PopoverOptions } from '@ionic/angular';
import { TObjectWithKeys } from '@monorepo/types';

type TDismissDetails = { data?: unknown; role?: string };
type ExactDismiss<T> = TDismissDetails & {
  [K in Exclude<keyof T, keyof TDismissDetails>]: never;
};

type TPopoverOptions<TProps extends object | void = void> = TProps extends void
  ? Omit<PopoverOptions, 'componentProps'>
  : Omit<PopoverOptions, 'componentProps'> & {
      componentProps: TObjectWithKeys<Extract<TProps, object>>;
    };

type TPopoverResult<TDismiss extends TDismissDetails = TDismissDetails> = {
  ionPopover: HTMLIonPopoverElement;
  present: () => Promise<void>;
  onDidDismiss: () => Promise<TDismiss>;
  onWillDismiss: () => Promise<TDismiss>;
};

/**
 * Type-safe wrapper around Ionic's `PopoverController`.
 *
 * - Defaults to `unknown` instead of Ionic's `any` for dismiss data.
 * - `componentProps` required only when `TProps` is specified, and must be an object.
 * - `TDismiss` constrains dismiss result to `{ data; role }` shape.
 * - If no props needed but dismiss data is typed, pass `void` as `TProps`.
 */
@Injectable({ providedIn: 'root' })
export class TypedPopoverController {
  constructor(private readonly popoverController: PopoverController) {}

  /**
   * Wrapper for Ionic PopoverController's create method.
   *
   * @example
   * ```ts
   * const popover = await this.typedPopoverService.create<
   *   { userId: string },
   *   { data: { confirmed: boolean }; role: 'save' | 'cancel' }
   * >({
   *   component: UserPopover,
   *   componentProps: { userId: 123 }, // TS Error: Type 'number' is not assignable to type 'string'.
   * });
   * await popover.present();
   * const { data, role } = await popover.onDidDismiss(); // data: { confirmed: boolean }, role: 'save' | 'cancel'
   * ```
   */
  async create<TProps extends object | void = void, TDismiss extends ExactDismiss<TDismiss> = TDismissDetails>(
    opts: TPopoverOptions<TProps>,
  ): Promise<TPopoverResult<TDismiss>> {
    const ionPopover = await this.popoverController.create(opts);
    return {
      ionPopover,
      present: () => ionPopover.present(),
      onDidDismiss: () => ionPopover.onDidDismiss() as Promise<TDismiss>,
      onWillDismiss: () => ionPopover.onWillDismiss() as Promise<TDismiss>,
    };
  }

  /**
   * [Shorthand] - Creates and immediately presents popover.
   *
   * @example
   * ```ts
   * // No props, untyped dismiss
   * const popover = await this.typedPopoverService.createAndPresent({ component: InfoPopover });
   *
   * // Typed dismiss, no props - use void
   * const popover = await this.typedPopoverService.createAndPresent<
   *   void,
   *   { data: { saved: boolean }; role: 'confirm' }
   * >({ component: InfoPopover });
   * ```
   */
  async createAndPresent<
    TProps extends object | void = void,
    TDismiss extends ExactDismiss<TDismiss> = TDismissDetails,
  >(opts: TPopoverOptions<TProps>): Promise<Omit<TPopoverResult<TDismiss>, 'present'>> {
    const { present, ...rest } = await this.create<TProps, TDismiss>(opts);
    await present();

    return rest;
  }

  /**
   * Wrapper for Ionic PopoverController's dismiss method.
   * When `TDismiss` is provided, `data` and `role` are required and strictly typed.
   *
   * @example
   * ```ts
   * // Untyped — all args optional
   * this.typedPopoverService.dismiss();
   *
   * // Typed — data and role required
   * this.typedPopoverService.dismiss<{ data: { confirmed: boolean }; role: 'save' | 'cancel' }>(
   *   { confirmed: true }, 'save',
   * );
   * this.typedPopoverService.dismiss<{ data: { confirmed: boolean }; role: 'save' | 'cancel' }>(
   *   { confirmed: true }, 'wrong', // TS error
   * );
   * ```
   */
  dismiss<TDismiss extends ExactDismiss<TDismiss> | void = void>(
    ...args: TDismiss extends TDismissDetails
      ? [data: TDismiss['data'], role: TDismiss['role'], id?: string]
      : [data?: unknown, role?: string, id?: string]
  ): ReturnType<PopoverController['dismiss']> {
    const [data, role, id] = args;
    return this.popoverController.dismiss(data, role, id);
  }

  getTop(): ReturnType<PopoverController['getTop']> {
    return this.popoverController.getTop();
  }
}
